import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import Select from 'react-select';
import { QueryBuilder } from 'react-querybuilder';
import { useNavigate } from 'react-router-dom';
import 'react-querybuilder/dist/query-builder.css';
import '../../Pages/automations.css';
import '../../Pages/edit.css';
import '../../Pages/details.css';
import Spinner from '../../Components/Spinner';
import Alert from '../../Components/Alert';
import { BASE_URL_API } from '../../Constants';
import { useAuth } from '../../hooks/AuthProvider';
import { getErrorMessage } from '../../utils/errorUtils';
import { buildDeviceDropdownOption, getDeviceDropdownId } from '../../utils/dropdownLabels';
import { LanguageContext } from '../../context/LanguageContext';
import automationsTranslations from '../../languages/automation.json';
import { ActionsIcon, AutomationHeaderIcon, TriggerIcon } from '../../svg/AutomationIcons';
import { DeviceIcon, OrganizationIcon } from '../../Icons';
import AutomationActionsBuilder from './AutomationActionsBuilder';
import {
  applyConditionFieldTypes,
  buildAutomationPayload,
  buildTemplateFields,
  collectActionIds,
  collectConditionIds,
  createEmptyConditions,
  createInitialAutomation,
  fixedField,
  getTemplateIdFromDevice,
  triggerOptions,
} from './automationHelpers';

const resolveDeviceOption = (options, automationDevice) =>
  options.find((option) => option.value === automationDevice?.did) ||
  (automationDevice?.did
    ? buildDeviceDropdownOption(
        {
          id: automationDevice?.device_id,
          did: automationDevice?.did,
          name: automationDevice?.device_name,
          organization_name: automationDevice?.organization_name,
        },
        'did'
      )
    : null);

const createAlertState = (isRtl) => ({
  open: false,
  message: '',
  type: 'info',
  position: isRtl ? 'left' : 'right',
});

const AutomationBuilderPage = ({ mode = 'create', automationId = null }) => {
  const isCreateMode = mode === 'create';
  const navigate = useNavigate();
  const { org, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const t = automationsTranslations[lang];
  const isRtl = ['ar', 'ur'].includes(lang);
  const requiredPermission = isCreateMode ? 'create_automations' : 'edit_automations';
  const hasPermission = Boolean(permissions?.[requiredPermission]);

  const [devices, setDevices] = useState([]);
  const [automation, setAutomation] = useState(createInitialAutomation(org));
  const [loading, setLoading] = useState(!isCreateMode);
  const [submitting, setSubmitting] = useState(false);
  const [alertState, setAlertState] = useState(createAlertState(isRtl));
  const [deviceDetailsByDid, setDeviceDetailsByDid] = useState({});
  const [templatesByDid, setTemplatesByDid] = useState({});
  const [persistedIds, setPersistedIds] = useState({
    conditionIds: new Set(),
    actionIds: new Set(),
  });

  const deviceDetailRequestsRef = useRef(new Map());
  const templateRequestsRef = useRef(new Map());

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      message,
      type,
      position: isRtl ? 'left' : 'right',
    });
  }, [isRtl]);

  const loadDevicesForOrganization = useCallback(async (organizationId) => {
    if (!organizationId) {
      setDevices([]);
      return [];
    }

    const response = await axios.get(`${BASE_URL_API}/device/filtered/${organizationId}`);
    const nextDevices = Array.isArray(response.data?.data) ? response.data.data : [];
    setDevices(nextDevices);
    return nextDevices;
  }, []);

  const ensureDeviceDetail = useCallback(async (did) => {
    if (!did) {
      return null;
    }

    if (deviceDetailsByDid[did]) {
      return deviceDetailsByDid[did];
    }

    if (deviceDetailRequestsRef.current.has(did)) {
      return deviceDetailRequestsRef.current.get(did);
    }

    const request = axios
      .get(`${BASE_URL_API}/device/get-did/${did}`)
      .then((response) => {
        const deviceData = response.data?.data ?? null;
        if (deviceData) {
          setDeviceDetailsByDid((current) => ({ ...current, [did]: deviceData }));
        }
        return deviceData;
      })
      .finally(() => {
        deviceDetailRequestsRef.current.delete(did);
      });

    deviceDetailRequestsRef.current.set(did, request);
    return request;
  }, [deviceDetailsByDid]);

  const ensureTemplateForDid = useCallback(async (did) => {
    if (!did) {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(templatesByDid, did)) {
      return templatesByDid[did];
    }

    if (templateRequestsRef.current.has(did)) {
      return templateRequestsRef.current.get(did);
    }

    const request = (async () => {
      const deviceData = await ensureDeviceDetail(did);
      const templateId = getTemplateIdFromDevice(deviceData);

      if (!templateId) {
        setTemplatesByDid((current) => ({ ...current, [did]: null }));
        return null;
      }

      const response = await axios.get(`${BASE_URL_API}/template/get/${templateId}`);
      const templateData = response.data?.data ?? null;
      setTemplatesByDid((current) => ({ ...current, [did]: templateData }));
      return templateData;
    })().finally(() => {
      templateRequestsRef.current.delete(did);
    });

    templateRequestsRef.current.set(did, request);
    return request;
  }, [ensureDeviceDetail, templatesByDid]);

  useEffect(() => {
    setAlertState(createAlertState(isRtl));
  }, [isRtl]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    setAutomation((current) => ({
      ...current,
      organization_id: org?.id ?? null,
      organization_name: org?.name ?? null,
    }));
  }, [isCreateMode, org?.id, org?.name]);

  useEffect(() => {
    if (!hasPermission) {
      return;
    }

    let isCancelled = false;

    const loadPageData = async () => {
      setLoading(true);

      try {
        if (isCreateMode) {
          await loadDevicesForOrganization(org?.id);
          return;
        }

        const automationResponse = await axios.get(`${BASE_URL_API}/automation/${automationId}`);
        const fetchedAutomation = automationResponse.data?.data;

        if (!fetchedAutomation || isCancelled) {
          return;
        }

        setAutomation(fetchedAutomation);
        setPersistedIds({
          conditionIds: collectConditionIds(fetchedAutomation.conditions),
          actionIds: collectActionIds(fetchedAutomation.actions),
        });

        await loadDevicesForOrganization(fetchedAutomation.organization_id);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        const errorMessage = getErrorMessage(error);
        showAlert('error', errorMessage);

        if (!isCreateMode && error.response?.status === 404) {
          setTimeout(() => navigate('/automations'), 3000);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadPageData();

    return () => {
      isCancelled = true;
    };
  }, [automationId, hasPermission, isCreateMode, loadDevicesForOrganization, navigate, org?.id, showAlert]);

  useEffect(() => {
    const didsToResolve = new Set([
      automation?.did,
      ...(Array.isArray(automation?.actions)
        ? automation.actions.map((action) => action?.did)
        : []),
    ].filter(Boolean));

    didsToResolve.forEach((did) => {
      ensureTemplateForDid(did).catch((error) => {
        console.error(`Failed to load template for device DID ${did}:`, error);
      });
    });
  }, [automation?.actions, automation?.did, ensureTemplateForDid]);

  const currentTemplate = automation?.did ? templatesByDid[automation.did] ?? null : null;
  const dynamicFields = useMemo(
    () => buildTemplateFields(automation?.trigger, currentTemplate),
    [automation?.trigger, currentTemplate]
  );
  const conditionFields = useMemo(
    () => (dynamicFields.length > 0 ? [...fixedField, ...dynamicFields] : fixedField),
    [dynamicFields]
  );

  const triggerSelectOptions = useMemo(
    () => triggerOptions.map((option) => ({ value: option, label: option })),
    []
  );
  const deviceSelectOptions = useMemo(
    () => devices.map((device) => buildDeviceDropdownOption(device, 'did')),
    [devices]
  );

  const selectedTriggerOption = triggerSelectOptions.find((option) => option.value === automation?.trigger) || null;
  const selectedDeviceOption = resolveDeviceOption(deviceSelectOptions, automation);
  const shouldShowDeviceSelect = Boolean(
    automation?.organization_id &&
    automation?.trigger !== 'manual' &&
    automation?.trigger !== 'schedule'
  );
  const shouldShowConditions =
    automation?.trigger === 'schedule' ||
    (automation?.trigger !== 'manual' && automation?.trigger !== 'schedule' && Boolean(automation?.did));

  const handleDescriptionChange = (event) => {
    const nextDescription = event.target.value;
    setAutomation((current) => ({
      ...current,
      description: nextDescription,
    }));
  };

  const handleTriggerChange = (selectedOption) => {
    const nextTrigger = selectedOption ? selectedOption.value : null;

    setAutomation((current) => ({
      ...current,
      trigger: nextTrigger,
      device_id: nextTrigger === 'manual' || nextTrigger === 'schedule' ? null : current.device_id,
      did: nextTrigger === 'manual' || nextTrigger === 'schedule' ? null : current.did,
      device_name: nextTrigger === 'manual' || nextTrigger === 'schedule' ? null : current.device_name,
      conditions: createEmptyConditions(),
    }));
  };

  const handleTriggerDeviceChange = (selectedOption) => {
    const nextDid = selectedOption?.value ?? null;

    setAutomation((current) => ({
      ...current,
      device_id: getDeviceDropdownId(selectedOption),
      device_name: selectedOption?.name || selectedOption?.baseLabel || null,
      did: nextDid,
      conditions: createEmptyConditions(),
    }));
  };

  const handleSave = async () => {
    const preparedAutomation = {
      ...automation,
      conditions: applyConditionFieldTypes(automation.conditions, conditionFields),
    };

    const payload = buildAutomationPayload(
      preparedAutomation,
      isCreateMode
        ? undefined
        : {
            persistedConditionIds: persistedIds.conditionIds,
            persistedActionIds: persistedIds.actionIds,
          }
    );

    setAutomation(preparedAutomation);
    setSubmitting(true);

    try {
      if (isCreateMode) {
        await axios.post(`${BASE_URL_API}/automation`, payload, {
          headers: { 'Content-Type': 'application/json' },
        });

        showAlert('success', t.createSuccess || 'Automation created successfully.');
      } else {
        const response = await axios.put(`${BASE_URL_API}/automation`, payload, {
          headers: { 'Content-Type': 'application/json' },
        });

        showAlert('success', response.data?.message || t.updateSuccess || 'Automation updated successfully.');
      }

      setTimeout(() => {
        navigate('/automations');
      }, 1600);
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const isSaveDisabled =
    submitting ||
    !Array.isArray(automation?.actions) ||
    automation.actions.length === 0 ||
    !String(automation?.description ?? '').trim() ||
    (shouldShowDeviceSelect && !automation?.did);

  if (!hasPermission) {
    return (
      <div className='configuration'>
        <div className='config-header'>
          <h2>Permission Denied</h2>
        </div>
        <div className='auto-permission-pad'>
          <p>
            {isCreateMode
              ? 'You do not have the required permission to create new automations.'
              : 'You do not have the required permission to edit automations.'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className='configuration devices-page auto-page' dir={isRtl ? 'rtl' : 'ltr'}>
      <Alert
        open={alertState.open}
        close={() => setAlertState((current) => ({ ...current, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className='device-main'>
        <div className='automation-builder-card auto-builder-card'>
          <div className='automation-builder-header auto-builder-header'>
            <div className='automation-builder-header-left auto-builder-header-left'>
              <div className='auto-builder-header-badge'>
                <AutomationHeaderIcon />
              </div>
              <div>
                <h2 className='auto-builder-title'>
                  {isCreateMode ? t.headerNew || t.buttonNew : t.headerEdit}
                </h2>
                <p className='auto-builder-subtitle'>
                  {t.headerSubtitle || 'Define triggers and actions to automate your IoT workflows'}
                </p>
              </div>
            </div>

            <div className='automation-builder-header-right auto-builder-header-right'>
              <button
                type='button'
                onClick={() => navigate('/automations')}
                className='auto-builder-back'
              >
                {t.buttonBack || 'Back'}
              </button>

              <button
                className={`devices-primary-btn edit-btn-save auto-builder-save ${isSaveDisabled ? 'disabled' : ''}`}
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                <SettingsSuggestIcon />
                <span>{isCreateMode ? t.buttonCreate : t.buttonSave}</span>
              </button>
            </div>
          </div>

          <div className='auto-builder-divider' />

          <div className='auto-builder-body'>
            <div className='automation-fields auto-builder-fields'>
              <div>
                <div className='edit-form-item auto-form-item'>
                  <label className='auto-form-label auto-form-label--withicon'>
                    <span className='auto-label-icon'>
                      <TriggerIcon />
                    </span>
                    <span>{t.labelTrigger}</span>
                  </label>
                  <Select
                    id='trigger'
                    options={triggerSelectOptions}
                    value={selectedTriggerOption}
                    onChange={handleTriggerChange}
                    classNamePrefix='react-select'
                  />
                </div>

                <div className='edit-form-item auto-form-item auto-form-item--spaced'>
                  <label className='auto-form-label'>
                    <span className='auto-label-icon'>
                      <OrganizationIcon />
                    </span>
                    <span>{t.labelOrganization}</span>
                  </label>
                  <label className='edit-form-input' name='myInput'>
                    {automation.organization_name ?? ''}
                  </label>
                </div>

                {shouldShowDeviceSelect ? (
                  <div className='edit-form-item auto-form-item auto-form-item--spaced'>
                    <label className='auto-form-label'>
                      <span className='auto-label-icon'>
                        <DeviceIcon />
                      </span>
                      <span>{t.labelDevice}</span>
                    </label>
                    <Select
                      id='devices'
                      options={deviceSelectOptions}
                      value={selectedDeviceOption}
                      onChange={handleTriggerDeviceChange}
                      classNamePrefix='react-select'
                    />
                  </div>
                ) : null}
              </div>

              <div className='edit-form-item auto-form-item'>
                <label className='auto-form-label'>{t.labelDescription}</label>
                <textarea
                  className='edit-form-input auto-desc-textarea'
                  name='automation-description'
                  value={automation.description ?? ''}
                  onChange={handleDescriptionChange}
                  placeholder={t.placeholderDescription}
                />
              </div>
            </div>

            {shouldShowConditions ? (
              <Fragment>
                <h3 className='qb-title'>{t.sectionConditions}</h3>

                <div className='qb-wrap'>
                  <QueryBuilder
                    fields={conditionFields}
                    query={automation.conditions}
                    onQueryChange={(query) => setAutomation((current) => ({ ...current, conditions: query }))}
                    showCloneButtons
                    showLockButtons
                    controlClassnames={{
                      queryBuilder: 'qb-root queryBuilder-branches justifiedLayout',
                      ruleGroup: 'qb-group',
                      header: 'qb-groupHeader',
                      body: 'qb-groupBody',
                      combinators: 'qb-select qb-select--combinator',
                      addRule: 'qb-btn qb-btn--add',
                      addGroup: 'qb-btn qb-btn--add',
                      cloneGroup: 'qb-btn qb-btn--icon',
                      lockGroup: 'qb-btn qb-btn--icon',
                      removeGroup: 'qb-btn qb-btn--danger qb-btn--icon',
                      rule: 'qb-rule',
                      field: 'qb-select qb-select--field',
                      operator: 'qb-select qb-select--operator',
                      value: 'qb-value',
                      valueSource: 'qb-select qb-select--source',
                      cloneRule: 'qb-btn qb-btn--icon',
                      lockRule: 'qb-btn qb-btn--icon',
                      removeRule: 'qb-btn qb-btn--danger qb-btn--icon',
                    }}
                  />
                </div>
              </Fragment>
            ) : null}

            <h3 className='auto-actions-title'>
              <span className='auto-label-icon'>
                <ActionsIcon />
              </span>
              <span>{t.sectionActions}</span>
            </h3>
            <AutomationActionsBuilder
              devices={devices}
              actions={automation.actions ?? []}
              setActions={(actions) => setAutomation((current) => ({ ...current, actions }))}
              templatesByDid={templatesByDid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationBuilderPage;
